import { useState } from 'react';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { FormError } from '@components/form-error';
import { IconBuilder } from '@components/icons/builder';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconPlaceholder } from '@components/icons/placeholder';
import { ListCard } from '@components/list-card';
import { RelativeSpinner } from '@components/loading-spinner';
import { SeparatorInline } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useListsScope, useTemplateUsageCounts, useUsage } from './integrations-hooks';
import { useIntegrationsApi } from './integrations-provider';
import type { IntegrationItemType, UsageJourney } from './integrations-types';

export type UsageCardProps = {
  itemType: IntegrationItemType;
  shortId: string;
  numJourneys?: number;
};

export const UsageCard = ({ itemType, shortId, numJourneys }: UsageCardProps) => {
  const { t } = useScopedTranslate(locale);
  const { usage: usageApi } = useIntegrationsApi();
  const { templateId } = useListsScope();

  const [isExpanded, setIsExpanded] = useState(false);

  const templateCounts = useTemplateUsageCounts(templateId);
  const usage = useUsage(itemType, shortId, { isEnabled: isExpanded });

  const isWebhook = itemType === 'webhook';
  const journeys = usage.isSuccess ? usage.data : [];
  // A missing row means zero; only a pending or failed load is unknown.
  const templateCount = templateCounts.isSuccess
    ? templateCounts.data.find((item) => item.type === itemType && item.shortId === shortId)
        ?.numJourneys ?? 0
    : undefined;
  const count = templateId ? templateCount : numJourneys;

  return (
    <CollapseCard
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      onVisibilityToggle={(isCollapsed) => {
        if (!isCollapsed) {
          setIsExpanded(true);
        }
      }}
      renderCollapse={() => (
        <Stack space={2}>
          {usage.isLoading && <RelativeSpinner />}
          {usage.isError && (
            <FormError
              isAnnounced
              text={isWebhook ? t('usageErrorWebhook') : t('usageErrorEndpoint')}
            />
          )}
          {usage.isSuccess && journeys.length === 0 && (
            <Text
              text={isWebhook ? t('usageEmptyWebhook') : t('usageEmptyEndpoint')}
              font="regular14"
              color="textSecondary"
            />
          )}
          {journeys.map((journey) => (
            <UsageJourneyRow
              key={journey.shortId}
              journey={journey}
              getJourneyBuilderUrl={usageApi.getJourneyBuilderUrl}
              getCompanyUrl={usageApi.getCompanyUrl}
            />
          ))}
        </Stack>
      )}
    >
      <Stack orientation="horizontal" align="center" space={2}>
        <IconPlaceholder color="elementPurple50" />
        <Text text={t('usage')} font="semibold14" />
        <Text text="•" color="textHelper" />
        <Text text={count ?? '–'} font="semibold14" />
      </Stack>
    </CollapseCard>
  );
};

const OpenJourneyBuilderButton = ({
  onClick,
  tooltip,
}: {
  onClick?: () => void;
  tooltip?: string;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Button
      isDisabled={!onClick}
      tooltip={tooltip}
      text={t('openJourneyBuilder')}
      textStyle={{ margin: `0 ${pxToRem(4)}` }}
      variant="ghost"
      size="sm"
      iconLeft={<IconBuilder />}
      iconRight={<IconOpenNewTab />}
      onClick={onClick}
    />
  );
};

type UsageJourneyRowProps = {
  journey: UsageJourney;
  getJourneyBuilderUrl: (workspaceId: string, journeyShortId: string) => string;
  getCompanyUrl?: (companyShortId: string) => string;
};

const UsageJourneyRow = ({
  journey,
  getJourneyBuilderUrl,
  getCompanyUrl,
}: UsageJourneyRowProps) => {
  const { t } = useScopedTranslate(locale);

  if (!journey.hasAccess) {
    return (
      <ListCard
        spaceX={4}
        spaceY={3}
        title={t('noAccess')}
        icon={<IconJourneyItem />}
        actions={<OpenJourneyBuilderButton tooltip={t('noAccessTooltip')} />}
      />
    );
  }

  const { workspaceId } = journey;
  const openBuilder = () => {
    if (workspaceId === null) {
      return;
    }
    return window.open(getJourneyBuilderUrl(workspaceId, journey.shortId), '_blank', 'noopener');
  };

  const actionsLabel = journey.numActions === 1 ? t('action') : t('actions');

  const { company } = journey;
  const companyUrl = company && getCompanyUrl?.(company.shortId);

  const description = company ? (
    <Stack space={0}>
      {journey.description && (
        <Text text={journey.description} font="regular14" color="textSecondary" />
      )}
      {companyUrl ? (
        <ActionText.ExternalLink
          href={companyUrl}
          font="regular12"
          color="textHelper"
          hoverColor="elementPurple100"
        >
          {company.name}
        </ActionText.ExternalLink>
      ) : (
        <Text text={company.name} font="regular12" color="textHelper" />
      )}
    </Stack>
  ) : (
    journey.description ?? undefined
  );

  return (
    <ListCard
      spaceX={4}
      spaceY={3}
      title={journey.title}
      description={description}
      icon={<IconJourneyItem />}
      actions={
        <>
          <ValueList items={[{ value: journey.numActions, label: actionsLabel }]} />
          {workspaceId !== null && (
            <>
              <SeparatorInline />
              <OpenJourneyBuilderButton onClick={openBuilder} />
            </>
          )}
        </>
      }
    />
  );
};

const locale = {
  en: {
    usage: 'Usage',
    usageEmptyEndpoint: 'This endpoint is currently not being used in any journey',
    usageEmptyWebhook: 'This webhook is currently not being used in any journey',
    usageErrorEndpoint: 'Failed to load journeys for this endpoint.',
    usageErrorWebhook: 'Failed to load journeys for this webhook.',
    action: 'Action',
    actions: 'Actions',
    openJourneyBuilder: 'Open Journey Builder',
    noAccess: '[No access]',
    noAccessTooltip: "You don't have access to this journey's workspace",
  },
};
